import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { Content } from 'src/contexts/ContentContext';
import { useLocalEventMobileScrollContext } from 'src/contexts/MobileSortAndFilterContext';
import * as Tabs from 'src/core/ui/Tabs';
import { EventTileInfoHeader } from 'src/tiles/EventTile/MobileEventTile.components';
import { Event, Listing, Performer, Venue } from 'src/WebApiController';

import { InventoryEventTabs } from '../../../InventoryEventTabs';
import { useInventoryEventLocalContext } from '../../InventoryEventLocalContext';
import { EventHeaderWithActionsMobile } from '../EventHeader/EventHeaderWithActionsMobile';
import * as styles from './AnimatedEventHeader.css';

type EventHeaderProps = {
  event: Event;
  venue?: Venue;
  performer?: Performer;
};

export const AnimatedEventHeaderContent = ({
  event,
  venue,
  performer,
}: EventHeaderProps) => {
  const { isAtTop } = useLocalEventMobileScrollContext();
  const timeString = useMemo(() => {
    return format(
      new Date(event?.dates?.start ?? new Date()),
      'h:mmaaa'
    ).toUpperCase();
  }, [event?.dates?.start]);

  const { isScrolledPastThreshold, headerContentAnimate, tabsAnimate } =
    useMemo(() => {
      // Use threshold-based animation instead of realtime offset
      const isScrolledPastThreshold = !isAtTop;

      // Binary state: either fully animated or not animated
      const headerContentAnimate = {
        opacity: isScrolledPastThreshold ? 1 : 0,
        y: isScrolledPastThreshold ? 0 : -48,
      };

      const tabsAnimate = {
        opacity: isScrolledPastThreshold ? 0 : 1,
        y: isScrolledPastThreshold ? -48 : 0,
      };

      return {
        isScrolledPastThreshold,
        headerContentAnimate,
        tabsAnimate,
      };
    }, [isAtTop]);

  return (
    <div className={styles.headerContentContainer}>
      {/* Header Content - appears on scroll */}
      <motion.div
        style={{
          display: isScrolledPastThreshold ? 'block' : 'none',
          pointerEvents: isScrolledPastThreshold ? 'auto' : 'none',
        }}
        animate={headerContentAnimate}
        transition={{ duration: 0.3 }}
      >
        <EventTileInfoHeader
          event={event}
          performer={performer}
          venue={venue}
          time={timeString}
        />
      </motion.div>

      {/* Event Tabs - disappears on scroll */}
      <motion.div
        style={{
          display: !isScrolledPastThreshold ? 'block' : 'none',
          pointerEvents: !isScrolledPastThreshold ? 'auto' : 'none',
        }}
        animate={tabsAnimate}
        transition={{ duration: 0.3 }}
      >
        <EventContentTabs />
      </motion.div>
    </div>
  );
};

const EventContentTabs = () => {
  const { activeTab, setActiveTab, eventContents } =
    useInventoryEventLocalContext();
  return (
    <Tabs.Root
      value={
        activeTab === InventoryEventTabs.ManualPricing
          ? InventoryEventTabs.Info
          : activeTab
      }
      onValueChange={(value) => {
        setActiveTab(value as InventoryEventTabs);
      }}
      className={styles.tabsSection}
    >
      <Tabs.List
        rootClassName={styles.tabsList}
        style={{ padding: 0, gap: 0 }}
        variant="pill"
        showScrollButton={false}
        scrollButtonClassName={styles.scrollButton}
      >
        {eventContents.map(({ id, contentId }) => (
          <Tabs.Trigger
            value={id}
            key={id}
            variant="pill"
            className={styles.tabsTrigger}
          >
            <Content id={contentId} />
          </Tabs.Trigger>
        ))}
      </Tabs.List>
    </Tabs.Root>
  );
};

export const AnimatedEventInfo = ({
  event,
  venue,
  performer,
  inventoryAccordionItem,
}: EventHeaderProps & {
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
}) => {
  const { isAtTop } = useLocalEventMobileScrollContext();

  const { isScrolledPastThreshold, animate } = useMemo(() => {
    // Use threshold-based animation instead of realtime offset
    const isScrolledPastThreshold = !isAtTop;

    // Binary state: either fully visible or hidden
    const animate = {
      opacity: isScrolledPastThreshold ? 0 : 1,
      y: isScrolledPastThreshold ? -72 : 0,
    };

    return {
      isScrolledPastThreshold,
      animate,
    };
  }, [isAtTop]);

  return (
    <motion.div
      style={{
        display: !isScrolledPastThreshold ? 'block' : 'none',
        pointerEvents: !isScrolledPastThreshold ? 'auto' : 'none',
      }}
      animate={animate}
      transition={{ duration: 0.5 }}
    >
      <EventHeaderWithActionsMobile
        event={event}
        performer={performer}
        venue={venue}
        inventoryAccordionItem={inventoryAccordionItem}
      />
    </motion.div>
  );
};
